<script setup lang="ts">
import { computed, toRefs } from 'vue';
import { storeToRefs } from 'pinia';
import useLocale from '../../composables/useLocale';
import useRenditionStore from '../../stores/rendition';
import useMediaStore from '../../stores/media';
import useReaderStore from '../../stores/reader';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import type { FontAwesomeIconSizes } from '../../types';
import { library } from '@fortawesome/fontawesome-svg-core';
import { faCirclePause, faCirclePlay, faPlay, faPause } from '@fortawesome/pro-solid-svg-icons';

library.add([faCirclePause, faCirclePlay, faPlay, faPause]);

type Emits = {
  (e: 'play-pause'): void;
};
const emit = defineEmits<Emits>();

type Props = {
  isPlaying?: boolean;
  showTooltips?: boolean;
  iconSize?: FontAwesomeIconSizes;
  buttonSize?: number;
  isLoading?: boolean;
};
const props = withDefaults(defineProps<Props>(), {
  showTooltips: true,
  iconSize: '2xl',
  buttonSize: 48,
  isLoading: false,
});
const { isPlaying, showTooltips, isLoading } = toRefs(props);

const { t } = useLocale();
const { isReflowable, locations } = storeToRefs(useRenditionStore());
const { hasMediaAvailable } = storeToRefs(useMediaStore());
const { isAudioOnly } = storeToRefs(useReaderStore());

type TextKey = { tooltip: string; label: string };

const textKey = computed((): TextKey => {
  if (!hasMediaAvailable.value) {
    return { label: 'book_player.message_play_pause_no_media_playback', tooltip: 'no_media_playback' };
  }
  return props.isPlaying
    ? { label: 'book_player.message_pause_playback', tooltip: 'button_web_pause' }
    : { label: 'book_player.message_start_playback', tooltip: 'button_web_play' };
});

const isDisabled = computed(() => {
  return (isReflowable.value && !locations.value) || !hasMediaAvailable.value;
});

const loading = computed(() => {
  return isLoading?.value || (isReflowable.value && !locations.value);
});

const vFocus = {
  mounted: (el: { focus: () => any }) => el.focus(),
};

const icon = computed(() => {
  if (isAudioOnly.value) {
    return isPlaying.value ? 'fa-solid fa-pause' : 'fa-solid fa-play';
  }
  return isPlaying.value ? 'fa-solid fa-circle-pause' : 'fa-solid fa-circle-play';
});
</script>

<template>
  <div class="d-inline-flex align-center">
    <v-btn
      id="play-pause-btn"
      data-testid="bp-play-pause-btn"
      :class="{
        'btn-disabled': isDisabled && !loading,
      }"
      v-focus
      icon
      color="on-brand"
      variant="text"
      :ripple="false"
      :size="buttonSize"
      :aria-label="t(textKey.label)"
      :aria-disabled="isDisabled"
      :loading="loading"
      @click="!isDisabled && emit('play-pause')"
    >
      <FontAwesomeIcon :icon="icon" :size="iconSize" class="play-pause-btn" />
    </v-btn>
    <v-tooltip
      v-if="!isDisabled"
      :disabled="!showTooltips"
      activator="#play-pause-btn"
      location="top"
      :text="t(textKey.tooltip)"
    />

    <span v-if="isDisabled" role="alert" aria-live="polite" class="d-sr-only">
      {{ t('book_player.message_play_pause_no_media_playback') }}
    </span>
  </div>
</template>

<style scoped lang="scss">
.btn-disabled {
  opacity: 0.26;
}
</style>
